import styles from './TextBuildingBlockPicker.module.scss';

import { Box } from '@mui/material';
import { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDataProvider } from '@work4all/data';

import { Group } from '@work4all/models/lib/Classes/Group.entity';
import { TextBuildingBlock } from '@work4all/models/lib/Classes/TextBuildingBlock.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { SelectableTree, TreeNode } from '../../dataDisplay/tree';
import { useGroups } from '../../hooks/use-groups';
import { GetEditorResult } from '../../input/format-text/TextEditor/types';

import { FilterTextInput } from './components';
import { IPickerProps } from './types';

export interface ITextBuildingBlockPickerProps<TMultiple extends boolean>
  extends Omit<IPickerProps<TextBuildingBlock, TMultiple>, 'onChange'> {
  editor?: GetEditorResult;
  onChange?: (props: {
    textBuildingBlock: TextBuildingBlock;
    newEditorValue: string;
  }) => void;
}

export function TextBuildingBlockPicker<TMultiple extends boolean>(
  props: ITextBuildingBlockPickerProps<TMultiple>
) {
  const { t } = useTranslation();
  const { editor, onChange } = props;
  const [searchTerm, setSearchTerm] = useState('');

  const handleChange = useCallback(
    (textBuildingBlock: TextBuildingBlock) => {
      setTimeout(() => {
        editor?.selection?.restore();
        editor?.events?.focus();

        const htmlContent = textBuildingBlock.text?.replace(/\n/g, '<br/>');
        editor?.html?.insert(htmlContent);

        if (onChange) {
          onChange({
            textBuildingBlock,
            newEditorValue: editor?.html.get(),
          });
        }
      }, 0);
    },
    [editor, onChange]
  );

  const request = useMemo<DataRequest>(() => {
    return {
      data: TEXT_BUILDING_BLOCK_REQUEST_DATA,
      entity: Entities.textBuildingBlock,
    };
  }, []);
  const response = useDataProvider<TextBuildingBlock>(request, false, 2000);

  const responseGroups = useGroups(Entities.textBuildingBlockGroup);

  const treeData = buildGroupTreeWithItems(
    responseGroups?.data?.groups,
    response.data,
    t,
    searchTerm
  );
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const isMultiLevelList = Boolean(
    treeData.find((item) => item.children?.length)
  );

  useEffect(() => {
    if (searchTerm.length) {
      setExpandedIds([
        ...(responseGroups?.data?.groups.map(
          (x) => 'group' + x.id.toString()
        ) ?? []),
        'group0',
      ]);
    } else {
      setExpandedIds([]);
    }
  }, [searchTerm.length, responseGroups?.data?.groups]);

  return (
    <div className={styles.container}>
      <Box className={styles.search}>
        <FilterTextInput
          autoFocus
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm?.(e);
          }}
          placeholder={t('INPUTS.SEARCH_TEXTBLOCKS')}
        />
      </Box>
      <Box className={styles.items}>
        <SelectableTree
          data={treeData}
          onNodeToggle={(_, nodeIds) => {
            setExpandedIds(nodeIds);
          }}
          expanded={expandedIds}
          selected={[]}
          multiple={false}
          selectable={isMultiLevelList ? 'leaf' : undefined}
          onChange={(nodeId) => {
            const value = response.data.find((x) => x.id.toString() === nodeId);
            handleChange(value);
          }}
        />
      </Box>
    </div>
  );
}
const TEXT_BUILDING_BLOCK_REQUEST_DATA: TextBuildingBlock = {
  id: null,
  name: null,
  text: null,
  rtfText: null,
  groupId: null,
  group: {
    id: null,
    name: null,
    index: null,
  },
};

const buildGroupTreeWithItems = (
  flatGroups: Group[] = [],
  items: TextBuildingBlock[],
  t: TFunction,
  searchTerm?: string
): TreeNode[] => {
  const sortedGroups = [...flatGroups].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const groupMap = new Map<
    number,
    { group: TreeNode; nestedGroups: TreeNode[]; items: TreeNode[] }
  >();
  const ungroupedItems: TreeNode[] = [];

  // Initialize groups and add them to a Map
  sortedGroups.forEach((group) => {
    const { name, id } = group ?? {};
    groupMap.set(id, {
      group: { id: 'group' + id.toString(), label: name, children: [] },
      nestedGroups: [],
      items: [],
    });
  });

  // Link child groups to parent groups
  sortedGroups.forEach((group) => {
    const parentEntry = groupMap.get(group?.parentId);
    const childEntry = groupMap.get(group?.id);
    if (parentEntry && childEntry) {
      parentEntry.nestedGroups.push(childEntry.group);
    }
  });

  // Filter items by search term if available
  const filteredItems = items
    ?.filter(
      (it) =>
        !searchTerm || it.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  // Attach items to their groups or collect as ungrouped
  filteredItems.forEach((item) => {
    const itemNode: TreeNode = {
      ...item,
      id: item.id.toString(),
      label: item.name,
    };

    const entry = groupMap.get(item.groupId);
    if (entry) {
      entry.items.push(itemNode);
    } else {
      ungroupedItems.push(itemNode);
    }
  });

  // Combine direct children before nested groups
  groupMap.forEach(({ group, items, nestedGroups }) => {
    group.children = [...items, ...nestedGroups];
  });

  const roots: TreeNode[] = [
    ...sortedGroups
      .filter((group) => group?.parentId === 0)
      .map((group) => {
        const entry = groupMap.get(group?.id);
        return entry?.group;
      })
      .filter((group): group is TreeNode => Boolean(group)),
    ...(ungroupedItems.length > 0
      ? [
          {
            id: 'group0',
            label: t('COMMON.WITHOUT_GROUP'),
            children: ungroupedItems,
          },
        ]
      : []),
  ];

  // Filter out empty groups (if search term is used)
  return roots.filter((r) => r.children?.length);
};
